import React from 'react';
import { PlayerEventAnalysis as PlayerEventAnalysisData } from '@/services/api';
import PitchZoneMap from './PitchZoneMap';
import MiniStat from './MiniStat';

type ZoneMap = Record<string, { count: number; label?: string; color: string; opacity: number }>;

const PassingSection: React.FC<{
  passing: PlayerEventAnalysisData['passing'];
  passZones: ZoneMap;
  directionEntries: Array<{ key: string; label: string }>;
}> = ({ passing, passZones, directionEntries }) => (
  <div className="mb-6">
    <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Passing</h5>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <PitchZoneMap zones={passZones} title="Pass Origins by Zone" />

      <div>
        {/* Direction breakdown */}
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Direction Breakdown</p>
        <div className="space-y-2 mb-4">
          {directionEntries.map(({ key, label }) => {
            const dir = passing.byDirection[key] || { count: 0, accurate: 0 };
            const pct = dir.count > 0 ? ((dir.accurate / dir.count) * 100).toFixed(0) : '0';
            const barWidth = passing.total > 0 ? (dir.count / passing.total) * 100 : 0;
            return (
              <div key={key}>
                <div className="flex justify-between text-xs text-gray-700 dark:text-gray-300 mb-0.5">
                  <span>{label}</span>
                  <span>{dir.count} ({pct}% acc)</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-blue-500 dark:bg-blue-400 rounded-full h-2 transition-all"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Key stats */}
        <div className="grid grid-cols-3 gap-2">
          <MiniStat label="Key Passes" value={passing.keyPasses} />
          <MiniStat label="Progressive" value={passing.progressivePasses} />
          <MiniStat label="Crosses" value={`${passing.crossesAccurate}/${passing.crosses}`} />
          <MiniStat label="Through Balls" value={passing.throughBalls} />
          <MiniStat label="Long Passes" value={`${passing.longAccurate}/${passing.longPasses}`} />
          <MiniStat label="Avg Length" value={`${passing.avgLength.toFixed(1)}m`} />
        </div>
      </div>
    </div>
  </div>
);

export default PassingSection;
